import {
  Bolton,
  ContactPreferenceValues,
  ContactPreferenceValuesV2,
  Plan,
  OrderCredit,
} from '@smarty-nx/data'
import {
  GROUP_INVITE_TYPES,
  getPlanCategory,
  getV1PlanCategory,
  getDiscountValue,
  getOfferName,
  returnCalculatedGross,
  returnCalculatedTax,
  returnV2CalculatedTax,
  returnInputAsBoolean,
} from '@smarty-nx/code-utils'

import { DATA_LAYER_JOURNEY_TYPES } from '../constants'
import { buildUser } from '../services'
import {
  AddonTransactionData,
  PaymentData,
  AddonProductObject,
  ProductObject,
  TransactionData,
  UserObject,
  GroupInfo,
} from '../types'

/**
 * Builds cart data from the V2 plan attributes, a checkout step and a checkout type
 * */
export const buildCartData = (
  plan: Plan,
  checkoutStep: number,
  checkoutType: DATA_LAYER_JOURNEY_TYPES
) => {
  if (plan) {
    return {
      checkout_step: checkoutStep,
      checkout_type: checkoutType,
      lines: [
        {
          product: buildProductData(plan),
        },
      ],
      value: {
        chargeType: 'monthly',
        displayGross: plan?.finalPriceWithoutCredit?.value,
        displayTax: returnV2CalculatedTax(plan?.finalPriceWithoutCredit?.value),
      },
    }
  }
  return null
}

/**
 * Builds cart data from the V2 plan attributes
 * */
export const buildProductData = (
  plan: Plan
): ProductObject | AddonProductObject | null => {
  if (plan) {
    return {
      id: plan.slug,
      name: plan.slug,
      category: getPlanCategory(plan),
      quantity: 1,
      value: {
        chargeType: 'monthly',
        displayGross: plan?.finalPriceWithoutCredit?.value,
        displayTax: returnV2CalculatedTax(plan?.finalPriceWithoutCredit?.value),
      },
      discount: {
        coupon: getOfferName(plan),
        value: { displayGross: getDiscountValue(plan) },
      },
    }
  }
  return null
}

/**
 * Builds addon data from the V2 add-on attributes
 * */
export const buildAddonProductData = (addon: Bolton) => {
  const { id, name, category, price_to_be_paid } = addon
  return {
    id,
    name,
    category,
    quantity: 1,
    value: {
      chargeType: 'once',
      displayGross: returnCalculatedGross(price_to_be_paid),
      displayTax: returnCalculatedTax(price_to_be_paid),
    },
  }
}

export const buildAddonCartData = (
  addon: Bolton,
  checkoutStep: number,
  checkoutType: DATA_LAYER_JOURNEY_TYPES
) => {
  if (addon) {
    return {
      checkoutStep,
      checkoutType,
      lines: [
        {
          product: buildAddonProductData(addon),
        },
      ],
      value: {
        chargeType: 'once',
        displayGross: returnCalculatedGross(addon?.price_to_be_paid),
        displayTax: returnCalculatedTax(addon?.price_to_be_paid),
      },
    }
  }
  return null
}

/**
 * Builds product data from the v2 plan attributes
 * */
export const buildV2ProductData = (plan: Plan, isPreloadedSim?: boolean) => {
  if (plan) {
    return {
      id: plan?.slug,
      name: plan?.slug,
      category: getV1PlanCategory(plan, isPreloadedSim),
      value: {
        chargeType: 'monthly',
        displayGross: plan?.finalPrice.value,
        displayTax: returnV2CalculatedTax(plan?.finalPrice.value),
      },
      discount: {
        coupon: getOfferName(plan),
        value: {
          displayGross: getDiscountValue(plan),
        },
      },
    }
  }
  return null
}

/**
 * Builds group object when it exists in local storage
 * */
export const buildGroupData = (
  inviteType: string | null = null
): GroupInfo | null => {
  const state = JSON.parse(localStorage.getItem('state') || '{}')
  if (state?.groupId) {
    return {
      group: {
        id: state.groupId,
        invitations: state.groupInvitations || 0,
        groupSize: state.groupSize || 0,
      },
      inviteType,
    }
  } else if (
    inviteType &&
    Object.values(GROUP_INVITE_TYPES).includes(inviteType)
  ) {
    return {
      group: {
        id: null,
        invitations: 0,
        groupSize: 0,
      },
      inviteType,
    }
  }
  return null
}

/**
 * Builds a user object and populates it with contact preferences data
 * Uses the same format as the contact preferences form:
 * email_marketing / sms_marketing / profiled_marketing / third_party_marketing
 */

export const buildPrefsString = (preferencesData: ContactPreferenceValues) => {
  if (preferencesData.push_notification_marketing !== undefined) {
    return `email_marketing:${returnInputAsBoolean(
      preferencesData.email_marketing
    )} | sms_marketing:${returnInputAsBoolean(
      preferencesData.sms_marketing
    )} | push_notification_marketing:${returnInputAsBoolean(
      preferencesData.push_notification_marketing
    )} | profiled_marketing:${returnInputAsBoolean(
      preferencesData.profiled_marketing
    )} | third_party_marketing:${returnInputAsBoolean(
      preferencesData.third_party_marketing
    )}`
  }
  return `email_marketing:${returnInputAsBoolean(
    preferencesData.email_marketing
  )} | sms_marketing:${returnInputAsBoolean(
    preferencesData.sms_marketing
  )} | profiled_marketing:${returnInputAsBoolean(
    preferencesData.profiled_marketing
  )} | third_party_marketing:${returnInputAsBoolean(
    preferencesData.third_party_marketing
  )}`
}

/**
 * Builds user.customer with marketing preferences data
 * Data is provided from form inputs on the contact preferences page
 * and all account creation journeys (web order, PayPal express,
 * token creation)
 * */
export const buildUserWithPrefs = (
  preferencesData: ContactPreferenceValues
) => {
  if (preferencesData) {
    return {
      customer: {
        marketing_preferences: buildPrefsString(preferencesData),
      },
    }
  }
  return null
}

export const buildUserWithPrefsV2 = (
  preferencesData: ContactPreferenceValuesV2
) => {
  if (preferencesData) {
    return {
      customer: {
        marketing_preferences: buildMarketingPrefsString(preferencesData),
      },
    }
  }
  return null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const remapMarketingPrefs = (prefs: { [key: string]: any }) => {
  const { third_party_marketing, ...rest } = prefs
  const partner_marketing = Boolean(third_party_marketing)
  return {
    ...rest,
    ...(typeof third_party_marketing !== 'undefined' && {
      partner_marketing,
    }),
  }
}

export const buildPaymentData = (orderCredit: OrderCredit): PaymentData[] => {
  const { payment_method, amount_paid, credit_used } = orderCredit
  const paymentData: PaymentData[] = []

  if (amount_paid || payment_method) {
    paymentData.push({
      type: payment_method,
      value: {
        displayGross: amount_paid,
      },
    })
  }

  if (credit_used) {
    paymentData.push({
      type: 'credit',
      value: {
        displayGross: credit_used,
      },
    })
  }

  return paymentData
}

export const buildTransactionData = ({
  id,
  orderCredit,
  plan,
  checkoutType,
  portInOption,
}: {
  id: string | null
  orderCredit: OrderCredit
  plan: Plan
  checkoutType: DATA_LAYER_JOURNEY_TYPES
  portInOption?: string
}): TransactionData => {
  return {
    ...(id && { id }),
    'lead-generator': orderCredit.lead_generator || null,
    ...(portInOption && { selected_port_in_option: portInOption }),
    payment: buildPaymentData(orderCredit),
    cart: {
      checkoutType,
      order_type: checkoutType,
      lines: [
        {
          product: {
            ...buildV2ProductData(plan),
            quantity: 1,
          },
        },
      ],
      value: {
        chargeType: 'monthly',
        displayGross: plan.finalPrice.value,
        displayTax: returnV2CalculatedTax(plan.finalPrice.value),
      },
    },
  }
}

export const buildAddonTransactionData = (
  id: string,
  orderCredit: OrderCredit,
  addon: Bolton
): AddonTransactionData => {
  const { price_to_be_paid } = addon
  return {
    transaction: {
      id,
      payment: buildPaymentData(orderCredit),
      cart: {
        checkoutType: DATA_LAYER_JOURNEY_TYPES.CHECKOUT_ADDON,
        lines: [
          {
            product: buildAddonProductData(addon),
          },
        ],
        value: {
          displayGross: returnCalculatedGross(price_to_be_paid).toFixed(2),
          displayTax: returnCalculatedTax(price_to_be_paid).toFixed(2),
        },
      },
    },
    user: buildUser() as UserObject,
  }
}

export const buildMarketingPrefsString = (
  dmpgMarketingPrefsAttrs = {}
): string =>
  Object.entries(dmpgMarketingPrefsAttrs)
    .map(([key, value]) => `${key}: ${Boolean(value)}`)
    .join('|')
